import React, { FormEvent } from "react";
import { useState, useEffect } from "react";
import {
  Box,
  Button,
  Container,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  TextField,
  InputAdornment,
  IconButton,
  Dialog,
  DialogTitle, DialogContent, DialogActions,
} from "@mui/material";
import { useDispatch } from "react-redux";
import moment from 'moment'
import { saveRequest, fetchRequest, deleteRequest } from "../../store/terminalBackground/actions";
import useSession from "../../effects/useSession";
import PageHeading from "../../common/pageHeading/PageHeading";
import pageStyles from "../../../js/pages/pageStyles";
import Layout from "../../pages/Layout";
import { Close, Search } from '@mui/icons-material';
import { get } from "lodash";
import useUserAccess from "../../effects/useUserAccess";
import NewBackGroupDialog from "./newBackgroundPopup";
import { toast } from "react-toastify";
import ProgressIndicator from "../../common/progressIndicator/ProgressIndicator";

const maxAllowedSize = 400 * 1024; // 400KB
const allowedFileTypes = ['image/jpeg', 'image/jpg', 'image/png'];

const TerminalBackground = () => {
  const session = useSession();
  const classes = pageStyles();
  const dispatch = useDispatch();
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = React.useState(0);
  const [total, setTotal] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const [showPreview, setShowPreview] = useState(false);
  const [currentImage, setCurrentImage] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [newImage, setNewImage] = useState({
    attributes: {
      FileName: "",
      Data: ""
    }
  });
  const inputFileRef = React.useRef<HTMLInputElement>();
  const [openDialog, setOpenDialog] = React.useState(false);

  const handleClose = () => {
    setOpenDialog(false);
  };

  const access = useUserAccess();
  const isCreateAllowed = get(access, `TerminalBackground.Access.C`);
  const isDeleteAllowed = get(access, `TerminalBackground.Access.U`);

  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number,
  ) => {
    setPage(newPage);
    const p = {
      page: {
        limit: String(rowsPerPage),
        offset: String(newPage * rowsPerPage),
      },
    }
    if (searchText) {
      p['filter'] = {
        'attributes.FileName': searchText
      }
    }
    onChangePage(p);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const newRowPerPage = parseInt(event.target.value);
    setRowsPerPage(newRowPerPage);
    setPage(0);
    const p = {
      page: {
        limit: String(newRowPerPage),
        offset: String(0),
      }
    }
    if (searchText) {
      p['filter'] = {
        'attributes.FileName': searchText
      }
    }
    onChangePage(p);
  };

  useEffect(() => {
    const p = {
      page: {
        limit: String(rowsPerPage),
        offset: String(rowsPerPage * page),
      }
    };
    if (searchText) {
      p['filter'] = {
        'attributes.FileName': searchText
      }
    }
    onChangePage(p);
  }, []);

  const onChangePage = (p) => {
    setLoading(true);
    dispatch(fetchRequest(null, p, (data, meta) => {
      if (Array.isArray(data)) {
        setImages(data);
      }
      setTotal(meta?.totalObjects || 0);
      setLoading(false);
    }));
  }

  const toBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
    })
  };

  const handleNewImage = async (event) => {
    const file = event.target.files[0];
    
    if (!file) {
      toast.error("Please select a file to upload");
      return;
    }

    // Check file type
    if (!allowedFileTypes.includes(file.type)) {
      const fileExtension = file.name.split('.').pop()?.toLowerCase();
      toast.error(`Invalid file type. File "${file.name}" has extension "${fileExtension}". Only JPEG (.jpg/.jpeg) and PNG (.png) files are allowed.`);
      return;
    }

    // Check file size
    if (file.size > maxAllowedSize) {
      const fileSizeKB = Math.round(file.size / 1024);
      toast.error(`File "${file.name}" is too large (${fileSizeKB}KB). Maximum allowed size is 400KB.`);
      return;
    }

    try {
      const base64Image = await toBase64(file);
      // add timezone to file name to make filename unique
      let filename = file.name.replace(/(\.[\w\d_-]+)$/i, `-${Date.now()}$1`);
      const newImage = {
        attributes: {
          FileName: filename.trim(),
          Data: base64Image as string
        }
      }
      setNewImage(newImage);
      setOpenDialog(true);
    } catch (error) {
      toast.error(`Failed to process file "${file.name}". Please try again or select a different file.`);
    }
  };

  const handleUploadImage = async (newImage) => {
    dispatch(saveRequest(newImage, (err, image) => {
      if (image && image.data) {
        setImages((prevImages) => [image.data, ...prevImages]);
        toast.success("Upload successfully!", {
          closeOnClick: true,
        });
      }
    }));
  }

  const handleImageDelete = (index, image) => {
    if (image && image.id) {
      dispatch(deleteRequest(image.id, (err, data) => {
        if (!err) {
          setImages((prevImages) => prevImages.filter((_, i) => i !== index));
          toast.success("Delete successfully!", {
            closeOnClick: true,
          });
        }
        // setShowConfirm(false);
      }))
    }
  };

  const handleClosePreview = () => {
    setCurrentImage({});
    setShowPreview(false);
  }

  const onSearchChange = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setPage(0);
    const p = {
      page: {
        limit: String(rowsPerPage),
        offset: String(0),
      },
    }
    if (searchText) {
      p['filter'] = {
        'attributes.FileName': searchText
      }
    }
    onChangePage(p);
  };

  const handleOnChange = (event: { target: { value: string } }) => {
    const { value } = event.target;
    setSearchText(value);
  }

  const previewImage = (image, index) => {
    setShowPreview(true);
    if (!image?.attributes?.Data) {
      dispatch(fetchRequest(image._id, {}, (data) => {
        if (data) {
          setCurrentImage({
            index: index,
            image: data
          })
        }
      }))
    } else {
      setCurrentImage({
        index: index,
        image: image
      })
    }
  }

  return (
    <Layout sessionData={session} key="company">
      <main className={classes.content as string}>
        <div className={classes.appBarSpacer as string} />
        <Container maxWidth="lg" className={classes.container as string}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Paper>
                <PageHeading
                  heading="Terminal Background"
                  entity="TerminalBackground"
                  primaryButtonText="Upload New Background"
                  primaryButtonClickHandler={() => {
                    if(inputFileRef?.current) {
                      inputFileRef.current.click();
                    }
                  }}
                />
              </Paper>
            </Grid>
            
            <Grid item xs={12}>
              <Paper variant="outlined" sx={{ p: 1.5, backgroundColor: '#f8f9fa' }}>
                <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                  <span style={{ fontSize: '14px', fontWeight: 500, color: '#666' }}>
                    📋 Upload Guidelines:
                  </span>
                </Box>
                <Box sx={{ fontSize: '13px', color: '#666', lineHeight: 1.3 }}>
                  • <strong>File Types:</strong> Only JPEG (.jpg/.jpeg) and PNG (.png) files are supported<br/>
                  • <strong>File Size:</strong> Maximum file size is 400KB<br/>
                  • <strong>Image Quality:</strong> For best results, use high-quality images with appropriate resolution
                </Box>
              </Paper>
            </Grid>

            <Grid item xs={12}>
              <Paper>
                <Box sx={{ p: 2, display: "flex", justifyContent: "flex-end" }}>
                  <div>
                    <input
                      accept=".jpg,.jpeg,.png"
                      style={{ display: "none" }}
                      id="image-upload"
                      type="file"
                      ref={inputFileRef}
                      onChange={handleNewImage}
                    />
                  </div>
                  <form onSubmit={(e) => onSearchChange(e)}>
                    <TextField
                      placeholder={"Search"}
                      variant="standard"
                      onChange={handleOnChange}
                      value={searchText}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton type="submit" edge="end">
                              <Search />
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </form>
                </Box>
                
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>File Name</TableCell>
                        <TableCell>Created Date</TableCell>
                        <TableCell>Action</TableCell>
                      </TableRow>
                    </TableHead>
                    {loading ? (
                        <ProgressIndicator />
                      ) : (
                    <TableBody>
                      {images.map((image, index) => (
                        <TableRow key={index}>
                          <TableCell>{image?.attributes?.FileName}</TableCell>
                          <TableCell>{image?.createdAt ? moment(image?.createdAt).format("DD/MM/YYYY, h:mm:ss a") : null}</TableCell>
                          <TableCell>
                            <Button
                              variant="outlined"
                              color="secondary"
                              size="small"
                              onClick={() => previewImage(image, index)}
                            >
                              View
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>)}
                  </Table>
                </TableContainer>
                <TablePagination
                  component="div"
                  count={total}
                  rowsPerPageOptions={[5, 10, 20]}
                  page={page}
                  onPageChange={handleChangePage}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                />
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </main>
      <Dialog
        maxWidth="md"
        // fullWidth={true}
        onClose={handleClosePreview}
        open={showPreview}
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <strong>
              {currentImage?.image?.attributes?.FileName || 'Terminal Background'}
            </strong>
            <IconButton edge="end" color="inherit" onClick={handleClosePreview} aria-label="close">
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers={true}>
          {currentImage && currentImage.image ? (
            <img style={{ maxHeight: "calc(100vh - 240px)"  }} alt={currentImage?.image?.attributes?.FileName} src={currentImage.image?.attributes?.Data} />
          ) : (
            <ProgressIndicator />
          )}
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            color="primary"
            size="large"
            onClick={handleClosePreview}
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
      <NewBackGroupDialog
        newImage={newImage}
        open={openDialog}
        onClose={handleClose}
        handleUploadImage={handleUploadImage}
      />
    </Layout>
  );
};

export default TerminalBackground;
